import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import Button from '../components/ui/Button';
import { isPremiumPlan } from '../lib/plans';

type StripePlan = 'premium_monthly' | 'premium_annual';

const premiumFeatures = [
  'AI Scanner avanzato',
  'Analytics premium',
  'Busta paga PDF con AI',
  'Categorie illimitate',
  'Supporto prioritario',
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user, session, profile } = useAuth();
  const [loadingPlan, setLoadingPlan] = useState<StripePlan | null>(null);

  const extractInvokeErrorMessage = async (error: unknown): Promise<string> => {
    if (!error || typeof error !== 'object') return 'Checkout non disponibile';

    const maybeContext = (error as { context?: Response }).context;
    if (maybeContext && typeof maybeContext.json === 'function') {
      try {
        const body = await maybeContext.json() as { error?: string; message?: string };
        if (body?.error) return body.error;
        if (body?.message) return body.message;
      } catch {
        // Fallback to default message below
      }
    }

    const msg = (error as { message?: string }).message;
    if (msg && !msg.includes('non-2xx')) return msg;
    return 'Pagamento temporaneamente non disponibile. Riprova tra poco.';
  };

  const startCheckout = async (plan: StripePlan) => {
    if (!user) {
      navigate('/register');
      return;
    }

    if (isPremiumPlan(profile?.plan)) {
      toast('Hai gia un piano Premium attivo');
      navigate('/settings');
      return;
    }

    setLoadingPlan(plan);
    try {
      const { data, error } = await supabase.functions.invoke('upgrade-plan', {
        body: { plan },
        headers: session?.access_token
          ? { Authorization: `Bearer ${session.access_token}` }
          : undefined,
      });

      if (error) throw error;
      if (!data?.url) throw new Error('Checkout URL non disponibile');

      window.location.href = data.url as string;
    } catch (err) {
      const message = await extractInvokeErrorMessage(err);
      toast.error(message);
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 px-4 py-10 sm:px-6">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <p className="inline-flex items-center gap-2 text-xs font-medium text-brand-300 bg-brand-500/10 border border-brand-500/30 rounded-full px-3 py-1 mb-4">
            <Sparkles size={12} />
            Monetizzazione Premium
          </p>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white">Scegli il tuo piano Premium</h1>
          <p className="text-slate-400 mt-2">Prova 7 giorni sul piano mensile. Passa ad annuale per il massimo risparmio.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card p-6 flex flex-col">
            <p className="text-sm text-slate-400">Premium Mensile</p>
            <div className="mt-2 flex items-end gap-1">
              <span className="text-4xl font-extrabold text-white">5€</span>
              <span className="text-slate-400 mb-1">/mese</span>
            </div>
            <p className="text-sm text-emerald-300 mt-2">Trial 7 giorni incluso</p>

            <ul className="space-y-2 my-6 flex-1">
              {premiumFeatures.map((feature) => (
                <li key={feature} className="flex items-start gap-2 text-sm text-slate-300">
                  <Check size={14} className="text-emerald-400 mt-0.5" />
                  {feature}
                </li>
              ))}
            </ul>

            <Button loading={loadingPlan === 'premium_monthly'} onClick={() => startCheckout('premium_monthly')}>
              Avvia trial mensile
            </Button>
          </div>

          <div className="rounded-2xl p-6 flex flex-col border-2 border-amber-400/50 bg-gradient-to-b from-amber-500/15 to-slate-900 shadow-lg shadow-amber-500/10">
            <div className="inline-flex self-start text-xs px-2.5 py-1 rounded-full bg-amber-400 text-amber-950 font-bold">
              Best Value 💰
            </div>
            <p className="text-sm text-amber-100/80 mt-3">Premium Annuale</p>
            <div className="mt-2 flex items-end gap-1">
              <span className="text-4xl font-extrabold text-white">40€</span>
              <span className="text-slate-300 mb-1">/anno</span>
            </div>
            <p className="text-sm text-amber-300 mt-2 font-medium">Risparmi 20€/anno</p>

            <ul className="space-y-2 my-6 flex-1">
              {premiumFeatures.map((feature) => (
                <li key={feature} className="flex items-start gap-2 text-sm text-slate-200">
                  <Check size={14} className="text-emerald-400 mt-0.5" />
                  {feature}
                </li>
              ))}
            </ul>

            <Button
              loading={loadingPlan === 'premium_annual'}
              onClick={() => startCheckout('premium_annual')}
              className="bg-amber-400 hover:bg-amber-300 text-amber-950"
            >
              Passa ad annuale
            </Button>
          </div>
        </div>

        <div className="mt-8 text-center text-sm text-slate-400">
          <Link to="/settings" className="text-brand-400 hover:text-brand-300">Torna alle impostazioni</Link>
        </div>
      </div>
    </div>
  );
}
